'use client';

import { PopStoreExtendedSubscriptionLevel } from '@ps/models';
import { Button } from '@ps/ui/atoms/button';

import { SubscriptionLevelBadge } from '../../subscription/components/SubscriptionLevelBadge';
import { formatTierMonthlyPrice } from '../functions/formatTierPrice';
import type { CreatorSubscriptionPlan } from '../types/CreatorSubscriptionPlan';
import { LongerCommitmentDiscountSelect } from './LongerCommitmentDiscountSelect';

interface Props {
  plan: CreatorSubscriptionPlan;
  selected: boolean;
  subscribed: boolean;
  isCurrentlySubscribedLevel: boolean;
  isMinimumSubscriptionLevel: boolean;
  isSubscriptionActive: boolean;
  disabled: boolean;
  /** Present only when this VIP plan is selected. */
  discountForLongerCommitment?: boolean;
  /** Follower tier only: show the "Follow" button (RN: `!subscribed`). */
  canFollow: boolean;
  /** Follower tier only: show the "Unfollow" button (RN:
   *  `isFollowing && !currentSubscriptionInfo` — i.e. a follow-only user). */
  canUnfollow: boolean;
  /** A follow/unfollow mutation is in flight. */
  pending: boolean;
  onSelect: (plan: CreatorSubscriptionPlan) => void;
  onFollow: () => void;
  onUnfollow: () => void;
  onDiscountForLongerCommitmentSelect: (selected: boolean) => void;
}

/**
 * One selectable tier row in the picker. Mirrors PopshopRN's `SubscriptionCell`:
 * badge + name (+ "Current Subscription" pill / "(Minimum)" hint) + price,
 * description, the free tier's inline Follow button, and the VIP longer-term
 * selector. Border turns mint when selected or when it's the active current tier.
 */
export function SubscriptionCell({
  plan,
  selected,
  subscribed,
  isCurrentlySubscribedLevel,
  isMinimumSubscriptionLevel,
  isSubscriptionActive,
  disabled,
  discountForLongerCommitment,
  canFollow,
  canUnfollow,
  pending,
  onSelect,
  onFollow,
  onUnfollow,
  onDiscountForLongerCommitmentSelect,
}: Props) {
  const isFollowerTier =
    plan.level === PopStoreExtendedSubscriptionLevel.FOLLOWER;
  const isVip = plan.level === PopStoreExtendedSubscriptionLevel.VIP;
  // "Current Subscription" badge — RN: `subscribed && isCurrentlySubscribedLevel`.
  const isCurrentSubscription = subscribed && isCurrentlySubscribedLevel;
  // Mint border — RN: `selected || (subscribed && isCurrentlySubscribedLevel &&
  // isSubscriptionActive)`.
  const highlighted =
    selected || (isCurrentSubscription && isSubscriptionActive);

  const price = isFollowerTier
    ? undefined
    : formatTierMonthlyPrice(plan.subscription);

  // RN also blocks tapping a subscribed-but-inactive lower tier, on top of the
  // `disabled` prop (`!canSelectPlan`).
  const notSelectable =
    disabled ||
    (subscribed && !isCurrentlySubscribedLevel && !isSubscriptionActive);

  const select = () => {
    if (!notSelectable) onSelect(plan);
  };

  return (
    // A clickable container, not a <button>: the Follower tier's "Follow" and
    // the VIP term selector render real <button>s inside, and a <button> can't
    // nest a <button> (hydration error). role/tabIndex/keydown keep it operable.
    <div
      role="button"
      tabIndex={notSelectable ? -1 : 0}
      aria-disabled={notSelectable}
      onClick={select}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          select();
        }
      }}
      className={`bg-surface-2 flex flex-col gap-2 rounded-lg border p-3 text-left ${
        notSelectable
          ? `pointer-events-none${isFollowerTier ? '' : 'opacity-60'}`
          : 'cursor-pointer'
      } ${highlighted ? 'border-brand border-2' : 'border-line-strong'}`}
    >
      {/* icon, name, price */}
      <div className="flex items-center gap-3">
        <div className="flex flex-1 items-center gap-1">
          <SubscriptionLevelBadge level={plan.level} iconOnly />
          <span className="text-content-1 mr-2 text-sm font-medium">
            {plan.name}
            {isMinimumSubscriptionLevel && (
              <span className="text-content-3 ml-1 text-xs opacity-50">
                (Minimum)
              </span>
            )}
          </span>
          {isCurrentSubscription && (
            <span className="bg-brand text-content-on-brand shrink-0 rounded-full px-2 py-0.5 text-[8px] font-light">
              Current Subscription
            </span>
          )}
        </div>
        {isFollowerTier ? (
          <span className="text-content-1 text-sm font-light">Free</span>
        ) : (
          <span className="flex items-center gap-1">
            <span className="text-content-1 text-sm font-light">
              {price?.amount}
            </span>
            <span className="text-content-3 text-xs font-light">
              {price?.currency}/mo.
            </span>
          </span>
        )}
      </div>

      {/* description — clamped to 4 lines unless this cell is selected
          (RN `numberOfLines={!selected ? 4 : undefined}`). */}
      <p
        className={`text-content-3 break-words text-xs font-light ${
          selected ? '' : 'line-clamp-4'
        }`}
      >
        {plan.description}
      </p>

      {/* Follower tier: Unfollow when following (follow-only user), Follow when
          not yet following — RN renders both on the card. `pointer-events-auto`
          re-enables the button even when the cell itself is `pointer-events-none`
          (a non-selectable Followers row). */}
      {isFollowerTier && (canUnfollow || canFollow) && (
        <span className="self-start">
          <Button
            variant="secondary"
            size="sm"
            disabled={pending}
            className="hover:bg-raise/80 text-sink pointer-events-auto bg-white"
            onClick={event => {
              event.stopPropagation();
              if (canUnfollow) onUnfollow();
              else onFollow();
            }}
          >
            {pending ? '…' : canUnfollow ? 'Unfollow' : 'Follow'}
          </Button>
        </span>
      )}

      {/* VIP longer-commitment discount */}
      {isVip && selected && (
        <LongerCommitmentDiscountSelect
          subscription={plan.subscription}
          selected={discountForLongerCommitment ?? false}
          onSelect={onDiscountForLongerCommitmentSelect}
        />
      )}
    </div>
  );
}
